//This Feature Name Config file for only operator role
/**
 * Provides an administration interface for managing feature and widget
 * display names available to the Operator role. Allows administrators to
 * customize multilingual feature labels, maintain English and French
 * translations, and control how widgets are presented throughout the
 * application while supporting change tracking, save operations, and
 * restoration of unsaved updates.
 */
import React, { useEffect, useState } from "react";

import { getWidgetNames, updateWidgetNames } from "@/services/commonconfig";

const widgetDisplayNames: Record<string, string> = {
  "components.activePrograms.title": "Active Programs",
  "components.analyticsInsights.financialAnalytics": "Analytics Insights",
  "components.disbursementRequestsWrapper.title": "Disbursement Request",
  "components.keyGuidelines.title": "Key Guidelines",
  "components.MeetingsWidget.title": "Meetings Widget",
  "components.mySubmission.title": "My Submission",
  "components.notificationTitle": "Workflow Notifications",
  "components.OversightWidget.title": "Oversight",
  "components.partnerDirectory.title": "Partner Directory",
  "components.partnerSubmission.title": "Partner Submission",
  "components.PipelinePriority.title": "Pipeline Priority",
  "components.taskPriorities.title": "Task Priorities",
  "components.widgets.financeTargets.title": "Finance Targets",
  "components.widgets.ideasTracker.title": "Ideas Tracker",
  "components.widgets.programImpact.title": "Program Impact",
  "components.widgets.programResources.title": "Program Resources",
  "components.widgets.quickAccessApps.title": "Quick Access Apps",
};

const FeatureNameConfig: React.FC = () => {
  const [widgets, setWidgets] = useState<any[]>([]);
  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    const data = await getWidgetNames();

    setWidgets(data);
    setInitial(JSON.stringify(data));
  };

  const hasChanges = initial !== "" && JSON.stringify(widgets) !== initial;

  const handleSave = async () => {
    try {
      setSaving(true);

      await updateWidgetNames(widgets);

      setInitial(JSON.stringify(widgets));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setWidgets(JSON.parse(initial));
  };

  const updateEnglish = (index: number, value: string) => {
    setWidgets((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        en: value,
      };

      return updated;
    });
  };

  const updateFrench = (index: number, value: string) => {
    setWidgets((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        fr: value,
      };

      return updated;
    });
  };

  return (
  <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-4">

      <div className="mt-1">
  <h2 className="text-base font-semibold text-gray-800">
    Feature Name Configuration
  </h2>
  <p className="text-sm text-gray-500 mt-0.5">
    Configure widget names displayed throughout the application.
  </p>
</div>

    <div className="grid grid-cols-[280px_1fr_1fr] gap-4 text-sm font-semibold text-gray-600 border-b border-gray-200 pb-2 mt-3">
  <div>Feature Name</div>
  <div>English</div>
  <div>French</div>
</div>
    {widgets.map((widget, index) => (
  <div
    key={`${widget.translation_key}-${index}`}
    className="grid grid-cols-[280px_1fr_1fr] gap-4 items-center border-b border-gray-100 py-1"
  >
    <div className="text-sm font-medium text-gray-700">
      {widgetDisplayNames[widget.translation_key] ??
        widget.translation_key}
    </div>

    <input
      type="text"
      value={widget.en ?? ""}
      onChange={(e) => updateEnglish(index, e.target.value)}
      className="h-8 w-full rounded-md border border-gray-300 px-3 text-sm"
    />

    <input
      type="text"
      value={widget.fr ?? ""}
      onChange={(e) => updateFrench(index, e.target.value)}
      className="h-8 w-full rounded-md border border-gray-300 px-3 text-sm"
    />
  </div>
))}

      <div className="flex justify-end gap-2 items-center">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved Changes
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 rounded text-xs"
          onClick={handleCancel}
          disabled={!hasChanges || saving}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 rounded text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default FeatureNameConfig;
